import { useCallback, useEffect, useRef, useState } from 'react';
import { PartOrientationsItem } from '../../../../contexts/LoadingPlanContext';
import loadingPlansService from '../../../../services/api/loadingPlansService';

/**
 * The engine's candidate orientations of the plan's parts, which only edit mode needs. They can run to
 * megabytes, so the server keeps them out of the loading plan payloads; they are fetched once per plan, when
 * editing starts.
 */
export function usePartsOrientations(loadingPlanId: number | undefined) {
  // Read through getPartsOrientations, so a caller that awaited ensurePartsOrientations sees the result at once.
  const loadedRef = useRef<{ planId: number; items: PartOrientationsItem[] } | null>(null);
  const planIdRef = useRef(loadingPlanId);
  planIdRef.current = loadingPlanId;
  const isMountedRef = useRef(true);
  const [isLoadingPartsOrientations, setIsLoadingPartsOrientations] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Never another plan's orientations.
  const getPartsOrientations = useCallback(
    (): PartOrientationsItem[] | null =>
      loadedRef.current && loadedRef.current.planId === planIdRef.current ? loadedRef.current.items : null,
    []
  );

  /**
   * Loads the orientations unless already loaded. Resolves to whether the caller should still go on (the
   * viewer may have unmounted while waiting). A failure is not fatal: edit mode then rotates in-plane only.
   */
  const ensurePartsOrientations = useCallback(async (): Promise<boolean> => {
    const planId = planIdRef.current;
    if (!planId || loadedRef.current?.planId === planId) {
      return true;
    }

    setIsLoadingPartsOrientations(true);
    try {
      const items = await loadingPlansService.fetchPartsOrientations(planId);
      loadedRef.current = { planId, items };
    } catch (err) {
      console.warn('Failed to fetch part orientations; editing with in-plane rotation only:', err);
    } finally {
      if (isMountedRef.current) {
        setIsLoadingPartsOrientations(false);
      }
    }
    return isMountedRef.current && planIdRef.current === planId;
  }, []);

  return { getPartsOrientations, ensurePartsOrientations, isLoadingPartsOrientations };
}
